/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Flex,
  Group, Modal,
  Text, TextInput,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Link, RichTextEditor } from "@mantine/tiptap";
import { useMutation } from "@tanstack/react-query";
import Highlight from "@tiptap/extension-highlight";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import { BubbleMenu, FloatingMenu, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import React, { useState } from "react";
import { queryClient, useCurrentUser } from "../../../../../hooks/QueryHooks";
import { createPlaybook } from "../../../../../services/playbook/playbookEndpoints";
import MButton from "../../../../../shared/ui/buttons/MButton";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import notify from "../../../../../utils/notify";
import PlaybookPreview from "./PlaybookPreview";

type Props = {
  children: React.ReactNode;
  entity_relation?: string;
  title?: string;
};

const CreatePlaybook = ({ title = "Create Playbook", children, entity_relation = "accounts" }: Props) => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const { data: user }: any = useCurrentUser({
  });
  const [opened, { open, close }] = useDisclosure(false);
  const [error, setError] = useState(false);
  const [playName, setPlayName] = useState("");
  const editor = useEditor({
    extensions: [
      Underline,
      Link,
      Highlight,
      TaskList.configure({
        HTMLAttributes: {
          class: "!pl-0",
        },
      }),
      TaskItem.configure({
        nested: true,
        HTMLAttributes: {
          class: "flex gap-3",
        },
      }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4],
          HTMLAttributes: {
            class: "text-2xl font-bold",
          },
        },
        code: {
          HTMLAttributes: {
            class: "bg-gray-100 text-sm p-2 rounded",
          },
        },
        bulletList:
        {
          HTMLAttributes: {
            class: "list-disc list-outside",
          },
        },
        orderedList:
        {
          HTMLAttributes: {
            class: "list-decimal list-outside",
          },
        },
      }),
    ],
  });

  const createPlaybookMutation = useMutation({
    mutationFn: createPlaybook,
  });

  const submitPlay = () => {
    if (!playName || !user?.user_id || !workspace?.id) {
      setError(true);
    } else {
      setError(false);
      createPlaybookMutation.mutate({
        title: playName,
        content: editor?.getHTML() || "",
        content_json: editor?.getJSON() || {},
        entity_relation,
        workspace_id: workspace?.id,
        owner_id: user?.user_id,
      }, {
        onSuccess: (data: any) => {
          queryClient.setQueryData(["all_playbooks", workspace?.id], (oldData: any) => ({
            ...oldData,
            data: [
              ...oldData.data,
              data?.data,
            ],
          }));
          notify({
            heading: "New Playbook Created",
            type: "success",
            messageList: ["You have successfully created a new Playbook."],
          });
          setPlayName("");
          close();
        },
        onError: (err: any) => {
          if (err.response?.status !== 0) {
            notify({
              heading: "Unknown error.",
              type: "failure",
              messageList: [
                "An unknown error occurred while creating the playbook. Please try again later or contact our support team for assistance.",
              ],
            });
          }
        },
      });
    }
  };

  return (
    <>
      <Modal
        padding="lg"
        classNames={{
          title: "text-gray-900 text-xl font-bold",
        }}
        size={700}
        opened={opened}
        centered
        onClose={() => {
          editor?.commands?.setContent("");
          close();
        }}
        title={title}
      >
        <TextInput
          value={playName}
          onChange={
          (event: any) => {
            setPlayName(event?.currentTarget.value);
          }
        }
          classNames={{
            label: "text-gray-500 font-bold mb-1",
          }}
          label="Play name"
          placeholder="Enter a play"
          error={error && playName === "" ? "Play name must not be empty" : null}
        />
        <Text size="sm" className="text-gray-500 font-bold mt-5">
          Content
        </Text>
        <RichTextEditor editor={editor}>
          {editor && (
          <>
            <BubbleMenu editor={editor}>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Bold />
                <RichTextEditor.Italic />
                <RichTextEditor.Link />
              </RichTextEditor.ControlsGroup>
            </BubbleMenu>
            <FloatingMenu editor={editor}>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.H1 />
                <RichTextEditor.H2 />
                <RichTextEditor.BulletList />
              </RichTextEditor.ControlsGroup>
            </FloatingMenu>
          </>
          )}
          <RichTextEditor.Toolbar sticky stickyOffset={60} p={16}>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Bold />
              <RichTextEditor.Italic />
              <RichTextEditor.Underline />
              <RichTextEditor.Strikethrough />
              <RichTextEditor.ClearFormatting />
              <RichTextEditor.Highlight />
              <RichTextEditor.Code />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.H1 />
              <RichTextEditor.H2 />
              <RichTextEditor.H3 />
              <RichTextEditor.H4 />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Blockquote />
              <RichTextEditor.Hr />
              <RichTextEditor.BulletList />
              <RichTextEditor.OrderedList />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Link />
              <RichTextEditor.Unlink />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.AlignLeft />
              <RichTextEditor.AlignCenter />
              <RichTextEditor.AlignJustify />
              <RichTextEditor.AlignRight />
            </RichTextEditor.ControlsGroup>
          </RichTextEditor.Toolbar>

          <RichTextEditor.Content />
        </RichTextEditor>
        <Text size="sm" className="text-gray-500 font-bold mt-5">
          Preview
        </Text>
        <PlaybookPreview playName={playName} htmlContent={editor?.getHTML() || ""} />
        <Flex justify="flex-end" gap={5} className="mt-4">
          <MButton
            onClick={() => {
              editor?.commands?.setContent("");
              close();
            }}
            variant="outline"
            className="hover:bg-gray-100"
            color="gray"
          >
            Cancel
          </MButton>
          <MButton
            color="purple"
            onClick={
              () => {
                submitPlay();
              }
          }
          >
            Save Play
          </MButton>
        </Flex>
      </Modal>
      <Group position="center" onClick={open}>
        {children}
      </Group>
    </>
  );
};

export default CreatePlaybook;
